import React from 'react';
import { motion } from 'framer-motion';
import { Target, BookOpen, Heart, Compass } from 'lucide-react';
import meenammalogobg from '@/images/meenammalogobg.png';

const MISSION_ITEMS = [
  {
    Icon: Target, color: 'text-brand-indigo', bg: 'bg-brand-indigo/10', border: 'border-brand-indigo/15',
    title: 'Our Purpose',
    text: 'We exist to eliminate educational barriers by providing comprehensive financial assistance and academic support to deserving students. Our scholarship programs specifically target learners from economically disadvantaged families, creating pathways to success throughout Srikakulam district.',
  },
  {
    Icon: BookOpen, color: 'text-brand-emerald', bg: 'bg-brand-emerald/10', border: 'border-brand-emerald/15',
    title: 'Our Vision',
    text: 'We aspire to build a society where every student, regardless of background, can access quality education and achieve their dreams. Our mission is to cultivate independent thinkers who will drive positive change in their communities.',
  },
  {
    Icon: Heart, color: 'text-brand-rose', bg: 'bg-brand-rose/10', border: 'border-brand-rose/15',
    title: 'Our Principles',
    text: 'Integrity, Excellence, Inclusivity, and Innovation guide our every action. We foster an environment that celebrates academic achievement and promotes continuous growth for all students.',
  },
];

const slideLeft  = { hidden: { opacity: 0, x: -40 }, visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } } };
const slideRight = { hidden: { opacity: 0, x:  40 }, visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } } };
const fadeUp = { hidden: { opacity: 0, y: -18 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55 } } };
const itemFade = (i) => ({ hidden: { opacity: 0, x: -16 }, visible: { opacity: 1, x: 0, transition: { delay: i * 0.14, duration: 0.5, ease: 'easeOut' } } });

const MissionSection = () => (
  <section id="mission" className="py-20 md:py-28 bg-gradient-to-b from-brand-indigo/6 to-background">
    <div className="container mx-auto px-4 sm:px-6">

      {/* Badge + Header */}
      <motion.div
        className="text-center mb-16"
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true }}
      >
        <div className="flex justify-center mb-4">
          <span className="section-badge">
            <Compass className="h-3 w-3" />
            Why We Exist
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold mb-5">
          Our <span className="gradient-text">Mission &amp; Vision</span>
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          Creating opportunities for every student to excel by providing resources and
          support needed to overcome financial obstacles.
        </p>
      </motion.div>

      {/* Two-column layout */}
      <div className="grid md:grid-cols-2 gap-14 lg:gap-20 items-center">

        {/* Logo showcase */}
        <motion.div
          className="relative flex items-center justify-center p-10 rounded-3xl overflow-hidden
                     border border-border/50 shadow-xl card-shine aspect-square md:aspect-video"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--brand-emerald)/0.08) 0%, hsl(var(--brand-amber)/0.06) 50%, hsl(var(--brand-indigo)/0.08) 100%)',
          }}
          initial="hidden"
          whileInView="visible"
          variants={slideLeft}
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Decorative blobs */}
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-brand-emerald/10 blur-2xl" />
          <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-brand-amber/12 blur-2xl" />
          {/* Deco ring */}
          <div className="absolute w-48 h-48 deco-ring opacity-40" />

          <motion.img
            src={meenammalogobg}
            alt="Meenamma Educational Trust"
            className="h-40 w-auto object-contain relative z-10"
            whileHover={{ scale: 1.06 }}
            transition={{ type: 'spring', stiffness: 250, damping: 20 }}
          />
        </motion.div>

        {/* Mission items */}
        <motion.div
          className="space-y-0"
          initial="hidden"
          whileInView="visible"
          variants={slideRight}
          viewport={{ once: true, amount: 0.2 }}
        >
          {MISSION_ITEMS.map(({ Icon, color, bg, border, title, text }, i) => (
            <motion.div
              key={title}
              className="relative"
              initial="hidden"
              whileInView="visible"
              variants={itemFade(i)}
              viewport={{ once: true, amount: 0.4 }}
            >
              {/* Connector line */}
              {i < MISSION_ITEMS.length - 1 && (
                <div className="absolute left-6 top-14 bottom-0 w-px bg-gradient-to-b from-border to-transparent" />
              )}

              <div className="flex gap-5 pb-8">
                {/* Icon */}
                <div className={`relative flex-shrink-0 p-3.5 ${bg} border ${border} rounded-2xl h-fit
                                 shadow-sm`}>
                  <Icon className={`h-5 w-5 ${color}`} />
                </div>

                {/* Content */}
                <div className="pt-1">
                  <h3 className="font-bold text-lg text-foreground mb-2 tracking-tight">{title}</h3>
                  <p className="text-muted-foreground text-[14px] leading-relaxed">{text}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

export default MissionSection;
